import { ref, computed, type Ref } from 'vue';
import type { FetchTicketResponse } from 'src/api/models';
import type { Column } from 'src/types/trello-board-type';
import { calcColumnWorkload, calcSeparatorWorkload } from 'src/utils/workload-calc';

export function useTrelloColumns(isWeekendVisible: Ref<boolean>, hasActiveFilters: Ref<boolean>) {
  const columns = ref<Column<FetchTicketResponse>[]>([]);
  const loadingColumns = ref<Set<string>>(new Set());
  const lastFetchedDate = ref<Date>(new Date());

  const visibleColumns = computed(() => {
    let filtered = columns.value;

    // Filter out weekends when not visible
    if (!isWeekendVisible.value) {
      filtered = filtered.filter((column) => {
        const dayOfWeek = column.date.getDay();
        return column.separator || (dayOfWeek >= 1 && dayOfWeek <= 5);
      });
    }

    // When filters are active, hide empty day columns and orphaned week separators
    if (hasActiveFilters.value) {
      // First pass: determine which day columns survive (non-empty)
      const survivingDayColumns = new Set<string>();
      const weekNumbersWithDays = new Set<number>();

      for (const column of filtered) {
        if (!column.separator && column.data.length > 0) {
          survivingDayColumns.add(column.id);
          weekNumbersWithDays.add(column.weekNumber);
        }
      }

      // Second pass: keep separators only if their week has surviving day columns
      filtered = filtered.filter((column) => {
        if (column.separator) {
          return weekNumbersWithDays.has(column.weekNumber);
        }
        return survivingDayColumns.has(column.id);
      });
    }

    return filtered;
  });

  function getDayName(date: Date) {
    const days = ["Zondag", "Maandag", "Dinsdag", "Woensdag", "Donderdag", "Vrijdag", "Zaterdag"];
    return days[date.getDay()];
  }

  function updateWorkload() {
    let currentWeekColumns: Column<FetchTicketResponse>[] = [];

    const columnsToProcess = isWeekendVisible.value
      ? columns.value
      : columns.value.filter(column => {
          if (column.separator) return true;
          const dayOfWeek = column.date.getDay();
          return dayOfWeek >= 1 && dayOfWeek <= 5;
        });

    columnsToProcess.forEach((column, index) => {
      if (!column.separator) {
        currentWeekColumns.push(column);
      }

      if (column.separator || index === columnsToProcess.length - 1) {
        // Compute workload for each day column using mechanics-aware calculation
        currentWeekColumns.forEach((col: Column<FetchTicketResponse>) => {
          const result = calcColumnWorkload(col.data);
          col.workload = result.workload;
          col.workloadOverflow = result.workloadOverflow;
          col.isUnplanned = result.isUnplanned;
          col.unassignedHours = result.unassignedHours;
          col.missingDurationCount = result.missingDurationCount;
          col.mechanicBreakdown = result.mechanicBreakdown;
        });

        // Compute separator (week average of non-unplanned days)
        if (column.separator) {
          const dayWorkloads = currentWeekColumns.map((col: Column<FetchTicketResponse>) => col.workload);
          const sepResult = calcSeparatorWorkload(dayWorkloads);
          column.workload = sepResult.workload;
          column.workloadOverflow = sepResult.workloadOverflow;
          column.isUnplanned = sepResult.isUnplanned;
          column.unassignedHours = 0;
          column.missingDurationCount = 0;
          column.mechanicBreakdown = [];
        }

        currentWeekColumns = [];
      }
    });
  }

  return {
    columns,
    loadingColumns,
    lastFetchedDate,
    visibleColumns,
    getDayName,
    updateWorkload
  };
}
